import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Add as AddIcon, TrackChanges as GoalIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useNavigationIntent } from '../../hooks/useNavigationIntent';
import { Goal } from '../../services/goalApi';
import { tokens } from '../../theme/tokens';
import { PlanningCard } from './PlanningCard';
import { GoalList } from './GoalList';
import { GoalPanel } from './GoalPanel';
import { useGoalsSection } from './useGoalsSection';

/** Set when a goal's page opens from here, so coming back lands on the goals again. */
export const GOALS_RETURN_KEY = 'financy:planning-return';

export const goalPath = (goalId: string) => `/planning/goals/${goalId}`;

/**
 * The goals section: a list of every goal of the current view and, on wide
 * screens, the goal picked from it opened in full beside it — its numbers,
 * its path or growth, its deposits month by month and its history. On
 * narrower screens a goal opens on its own page instead. Who may do what
 * follows the same permission grammar the bills card follows.
 */
export const SavingsGoalsCard: React.FC = () => {
  const { t } = useTranslation('planning');
  const theme = useTheme();
  const wide = useMediaQuery(theme.breakpoints.up('lg'));
  const navigate = useNavigate();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  const section = useGoalsSection({ onCreated: (goal) => setSelectedId(goal.id) });
  const { activeGoals, archivedGoals, trails, isLoading, error, now } = section;
  const selected = activeGoals.find((goal) => goal.id === selectedId) ?? activeGoals[0] ?? null;

  // "Registrar → Guardar numa meta" lands here with the form already open.
  useNavigationIntent('open', 'goal', section.openCreate);

  // Back from a goal's page: bring the goals into view once they are drawn.
  useEffect(() => {
    if (isLoading) return;
    let returning = false;
    try {
      returning = sessionStorage.getItem(GOALS_RETURN_KEY) === '1';
      sessionStorage.removeItem(GOALS_RETURN_KEY);
    } catch {
      returning = false;
    }
    if (returning) sectionRef.current?.scrollIntoView?.({ block: 'start' });
  }, [isLoading]);

  const openGoal = (goal: Goal) => {
    if (wide) {
      setSelectedId(goal.id);
      return;
    }
    try {
      sessionStorage.setItem(GOALS_RETURN_KEY, '1');
    } catch {
      // without storage the page still opens; coming back just starts at the top
    }
    navigate(goalPath(goal.id), { state: { fromPlanning: true } });
  };

  const list = (
    <GoalList
      goals={activeGoals}
      archived={archivedGoals}
      trails={trails}
      selectedId={selected?.id ?? null}
      highlightSelected={wide}
      onSelect={openGoal}
      now={now}
      canCreate={section.canEdit}
      onCreate={section.openCreate}
      canReorder={section.canEdit && activeGoals.length > 1}
      onReorder={section.reorder}
      canModify={section.canModify}
      onUnarchive={section.unarchiveGoal}
      onMenu={section.openMenu}
    />
  );

  let body: React.ReactNode;
  if (isLoading && section.goals.length === 0) {
    body = (
      <PlanningCard title={t('goals.title')}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      </PlanningCard>
    );
  } else if (activeGoals.length === 0) {
    body = (
      <Box sx={{ display: 'grid', gap: 2.5 }}>
        <PlanningCard title={t('goals.title')}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 1.5, py: 4 }}>
            <GoalIcon sx={{ fontSize: 40, color: tokens.accent, opacity: 0.5 }} />
            <Typography sx={{ fontSize: 14, color: tokens.ink3, maxWidth: 320 }}>{t('goals.empty')}</Typography>
            {section.canEdit && (
              <Button variant='contained' startIcon={<AddIcon />} onClick={section.openCreate} sx={{ minHeight: 44 }}>
                {t('goals.add')}
              </Button>
            )}
          </Box>
        </PlanningCard>
        {archivedGoals.length > 0 && list}
      </Box>
    );
  } else if (wide && selected) {
    body = (
      <Box sx={{ display: 'grid', gap: 2.5, alignItems: 'start', gridTemplateColumns: '320px minmax(0, 1fr)' }}>
        {list}
        <GoalPanel
          goal={selected}
          trail={trails[selected.id] ?? []}
          now={now}
          showPeople={section.inGroup}
          canContribute={section.canEdit && !selected.isAchieved}
          canAdjust={section.canEdit}
          canModify={section.canModify(selected)}
          canUndo={section.canUndo}
          onContribute={() => section.openContribute(selected)}
          onAdjust={() => section.openAdjust(selected)}
          onMenu={(element) => section.openMenu(element, selected)}
          onUndo={(entry) => section.removeContribution(selected, entry)}
        />
      </Box>
    );
  } else {
    body = list;
  }

  return (
    <Box ref={sectionRef} sx={{ scrollMarginTop: 88 }}>
      {error && (
        <Alert severity='error' sx={{ mb: 2 }} onClose={section.clearError}>
          {error}
        </Alert>
      )}
      {body}
      {section.dialogs}
    </Box>
  );
};
